import {
  addDays,
  monthGridStart,
  startOfMonth,
  startOfWeek,
} from "../../lib/dateNav";
import { todayKey } from "../../lib/format";

export const CALENDAR_VISIBLE_WEEK_ROWS = 6;
export const CALENDAR_WEEK_BUFFER = 12;
export const CALENDAR_PREPEND_THRESHOLD_ROWS = 3;
/** Pre-measure fallback so the viewport never expands to full week list height. */
export const CALENDAR_FALLBACK_ROW_HEIGHT_PX = 42;

export function buildWeekStartsRange(firstWeek: string, lastWeek: string): string[] {
  if (firstWeek > lastWeek) return [];
  const weeks: string[] = [];
  let cursor = firstWeek;
  while (cursor <= lastWeek) {
    weeks.push(cursor);
    cursor = addDays(cursor, 7);
  }
  return weeks;
}

export function buildWeekDays(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
}

export function buildInitialWeekStarts(
  anchorDate: string,
  referenceDate: string,
  bufferWeeks = CALENDAR_WEEK_BUFFER,
): string[] {
  const anchorWeek = startOfWeek(anchorDate);
  const todayWeek = startOfWeek(referenceDate);
  const firstWeek = addDays(anchorWeek, -bufferWeeks * 7);
  return buildWeekStartsRange(firstWeek, todayWeek);
}

export function weekIndexForDate(weekStarts: readonly string[], date: string): number {
  const week = startOfWeek(date);
  return weekStarts.indexOf(week);
}

export function resolveDominantMonthFromDays(days: readonly string[]): string {
  if (days.length === 0) return startOfMonth(todayKey());
  const counts = new Map<string, number>();
  const firstIndex = new Map<string, number>();
  days.forEach((day, index) => {
    const month = startOfMonth(day);
    counts.set(month, (counts.get(month) ?? 0) + 1);
    if (!firstIndex.has(month)) firstIndex.set(month, index);
  });
  let dominant = startOfMonth(days[0]!);
  let maxCount = -1;
  let bestFirst = Number.POSITIVE_INFINITY;
  for (const [month, count] of counts) {
    const monthFirst = firstIndex.get(month)!;
    if (count > maxCount || (count === maxCount && monthFirst < bestFirst)) {
      maxCount = count;
      bestFirst = monthFirst;
      dominant = month;
    }
  }
  return dominant;
}

export function getVisibleWeekRowRange(
  totalWeeks: number,
  scrollTop: number,
  rowHeight: number,
  viewportHeight: number,
): { firstRow: number; lastRow: number } {
  if (totalWeeks <= 0 || rowHeight <= 0) return { firstRow: 0, lastRow: -1 };
  const visibleRowCount = Math.ceil(viewportHeight / rowHeight);
  const lastRow = totalWeeks - 1 - Math.floor(scrollTop / rowHeight);
  const firstRow = Math.max(0, lastRow - visibleRowCount + 1);
  return { firstRow, lastRow };
}

export function getVisibleDaysInViewport(
  weekStarts: readonly string[],
  scrollTop: number,
  rowHeight: number,
  viewportHeight: number,
): string[] {
  if (rowHeight <= 0 || weekStarts.length === 0) return [];
  const { firstRow, lastRow } = getVisibleWeekRowRange(
    weekStarts.length,
    scrollTop,
    rowHeight,
    viewportHeight,
  );
  const days: string[] = [];
  for (let row = firstRow; row <= lastRow && row < weekStarts.length; row++) {
    days.push(...buildWeekDays(weekStarts[row]!));
  }
  return days;
}

export function resolveDominantMonthAtScrollPosition(
  weekStarts: readonly string[],
  scrollTop: number,
  rowHeight: number,
  viewportHeight: number,
): string {
  return resolveDominantMonthFromDays(
    getVisibleDaysInViewport(weekStarts, scrollTop, rowHeight, viewportHeight),
  );
}

/** Flipped vertical scroll: swipe up decreases scrollTop → earlier weeks. */
export function flippedScrollTopForWeekIndex(
  weekIndex: number,
  totalWeeks: number,
  rowHeight: number,
  viewportHeight: number,
  align: "start" | "center" | "end" = "center",
): number {
  if (totalWeeks <= 0 || rowHeight <= 0) return 0;
  const totalHeight = totalWeeks * rowHeight;
  const maxScroll = Math.max(0, totalHeight - viewportHeight);
  const rowFromBottom = (totalWeeks - 1 - weekIndex) * rowHeight;
  let scrollTop: number;
  if (align === "end") {
    scrollTop = rowFromBottom;
  } else if (align === "start") {
    scrollTop = rowFromBottom - (viewportHeight - rowHeight);
  } else {
    scrollTop = rowFromBottom - (viewportHeight - rowHeight) / 2;
  }
  return clamp(scrollTop, 0, maxScroll);
}

export function maxFlippedScrollTop(totalWeeks: number, rowHeight: number, viewportHeight: number): number {
  return Math.max(0, totalWeeks * rowHeight - viewportHeight);
}

export function shouldPrependOlderWeeks(
  scrollTop: number,
  maxScrollTop: number,
  rowHeight: number,
  thresholdRows = CALENDAR_PREPEND_THRESHOLD_ROWS,
): boolean {
  return scrollTop >= maxScrollTop - thresholdRows * rowHeight;
}

export function prependWeekCount(currentFirstWeek: string, weeksToAdd: number): string[] {
  const prepended: string[] = [];
  let cursor = addDays(currentFirstWeek, -7);
  for (let index = 0; index < weeksToAdd; index++) {
    prepended.unshift(cursor);
    cursor = addDays(cursor, -7);
  }
  return prepended;
}

export function ensureWeekStartsCoverMonth(
  weekStarts: readonly string[],
  month: string,
  referenceDate: string,
  batchWeeks = CALENDAR_WEEK_BUFFER,
): string[] {
  const monthStartWeek = monthGridStart(month);
  const todayWeek = startOfWeek(referenceDate);
  let result = weekStarts.length > 0 ? [...weekStarts] : buildInitialWeekStarts(monthStartWeek, referenceDate);

  while (result.length > 0 && result[0]! > monthStartWeek) {
    result = [...prependWeekCount(result[0]!, batchWeeks), ...result];
  }

  const lastWeek = result.at(-1);
  if (!lastWeek || lastWeek < todayWeek) {
    const tailStart = lastWeek ? addDays(lastWeek, 7) : monthStartWeek;
    result = [...result, ...buildWeekStartsRange(tailStart, todayWeek)];
  }

  return result;
}

export function heatmapRangeForWeekStarts(
  weekStarts: readonly string[],
  referenceDate: string,
): { startDate: string; endDate: string } {
  if (weekStarts.length === 0) {
    return { startDate: referenceDate, endDate: referenceDate };
  }
  const startDate = weekStarts[0]!;
  const lastWeekEnd = addDays(weekStarts[weekStarts.length - 1]!, 6);
  const endDate = lastWeekEnd > referenceDate ? referenceDate : lastWeekEnd;
  return { startDate, endDate };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
